import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Search } from 'lucide-react'

import { Input } from '@/shared/components/ui/input'
import { useProducts } from '@/admin/hooks/use-products'
import { cn } from '@/shared/lib/utils'
import type { Product } from '@/admin/types/products'

export interface SkuVariantMatch {
  uuid: string
  sku: string
  product_line: string
  product_name: string
  color: string
}

interface Props {
  value: string
  onChange: (value: string) => void
  onSelectVariant: (variant: SkuVariantMatch) => void
  placeholder?: string
  disabled?: boolean
  'data-testid'?: string
}

function extractVariantMatch(p: Product): SkuVariantMatch | null {
  const sku = ((p.product_data?.sku as string | undefined) ?? '').trim()
  if (!sku) return null
  const color =
    ((p.product_data?.color as string | undefined) ?? '').trim() ||
    p.cached_short_name
  const product_line = p.product_template?.name ?? ''
  // Variants carry their parent_product (the VARIANT_BASE / PRODUCT row)
  // whose cached_short_name is the "product name" the dropdowns expect.
  const product_name =
    p.parent_product?.cached_short_name ?? p.cached_short_name ?? ''
  return { uuid: p.uuid, sku, product_line, product_name, color }
}

export function SkuSearchCombobox({
  value,
  onChange,
  onSelectVariant,
  placeholder = 'Type SKU to search…',
  disabled,
  'data-testid': testId,
}: Props) {
  const [open, setOpen] = useState(false)
  const [debouncedQuery, setDebouncedQuery] = useState(value)
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Debounce so we don't fire a request on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(value), 250)
    return () => clearTimeout(handle)
  }, [value])

  const trimmed = debouncedQuery.trim()
  // Only query while the dropdown is open - keeps us from firing requests
  // when the SKU is set programmatically (e.g. via the color→SKU autofill
  // path in the parent form).
  const enabled = open && trimmed.length >= 1
  const params = useMemo(
    () =>
      enabled
        ? ({
            product_data__sku__icontains: trimmed,
            exclude_variants: 'false',
            page_size: '20',
          } as Record<string, string>)
        : undefined,
    [enabled, trimmed],
  )
  const { data, isFetching } = useProducts(params, { enabled })

  const results = useMemo<SkuVariantMatch[]>(() => {
    const items = data?.results ?? []
    const seen = new Set<string>()
    const out: SkuVariantMatch[] = []
    for (const p of items) {
      const match = extractVariantMatch(p)
      if (!match) continue
      // Dedupe by uuid in case the server returns the same variant via
      // parent + child rows.
      if (seen.has(match.uuid)) continue
      seen.add(match.uuid)
      out.push(match)
    }
    return out
  }, [data])

  // Close on outside click. Listening on mousedown so a click on a result
  // inside the dropdown still registers before the wrapper sees the blur.
  useEffect(() => {
    if (!open) return
    function handleMouseDown(event: MouseEvent) {
      if (!wrapperRef.current) return
      if (!wrapperRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [open])

  function handleSelect(match: SkuVariantMatch) {
    onChange(match.sku)
    onSelectVariant(match)
    setOpen(false)
  }

  const showDropdown = open && enabled
  const showEmpty = showDropdown && !isFetching && results.length === 0

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-[var(--ink-3)]"
          aria-hidden
        />
        <Input
          data-testid={testId}
          value={value}
          onChange={(e) => {
            onChange(e.target.value)
            if (!open) setOpen(true)
          }}
          onFocus={() => {
            if (value.trim().length > 0) setOpen(true)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false)
          }}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className="pl-8"
        />
        {isFetching && enabled && (
          <Loader2
            className="absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 animate-spin text-[var(--ink-3)]"
            aria-hidden
          />
        )}
      </div>

      {showDropdown && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[280px] overflow-y-auto rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] p-1 shadow-[var(--shadow-float)]"
        >
          {isFetching && results.length === 0 && (
            <div className="px-2.5 py-3 text-center text-[12px] text-[var(--ink-3)]">
              Searching…
            </div>
          )}
          {showEmpty && (
            <div className="px-2.5 py-3 text-center text-[12px] text-[var(--ink-3)]">
              No matches for &ldquo;{trimmed}&rdquo;
            </div>
          )}
          {results.map((match) => (
            <button
              key={match.uuid}
              type="button"
              role="option"
              onClick={() => handleSelect(match)}
              className={cn(
                'flex w-full flex-col items-start gap-0.5 rounded-md px-2.5 py-2 text-left transition-colors',
                'hover:bg-[var(--bg-subtle)] focus:bg-[var(--bg-subtle)] focus:outline-none',
              )}
            >
              <span className="text-[13px] font-medium text-[var(--ink-1)]">
                {match.sku}
              </span>
              <span className="text-[11px] text-[var(--ink-3)]">
                {[match.product_line, match.product_name, match.color]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
